import { useCallback, useEffect, useRef, useState } from 'react';
import { appSections } from '../data/apps';
import supabase from '../supabase/supabase';
import {
  createApplication,
  createQuickLaunchApplication,
  createSection as createRemoteSection,
  createVaultTool,
  deleteApplication,
  deleteSection as deleteRemoteSection,
  deleteVaultTool as deleteVaultToolRemote,
  importLegacyWorkspace,
  loadWorkspace,
  moveApplication,
  moveApplicationToVault,
  pinApplication,
  renameSection as renameRemoteSection,
  reorderQuickLaunch,
  reorderSection,
  restoreVaultApplication,
  unpinApplication,
  updateApplication,
  updateVaultTool as updateRemoteVaultTool
} from '../services/tefolioData';

const STORAGE_KEY = 'tefolio.sections.v1';
const QUICK_LAUNCH_KEY = 'tefolio.quick-launch.v1';
const QUICK_APPS_KEY = 'tefolio.quick-apps.v1';
let mutationQueue = Promise.resolve();

function createId(prefix) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function seedSections() {
  return appSections.map((section, index) => ({
    id: `sec-seed-${index + 1}`,
    order: index,
    title: section.title,
    apps: section.apps.map((app, appIndex) => ({
      ...app,
      id: `app-seed-${index + 1}-${appIndex + 1}`
    }))
  }));
}

function normalize(sections) {
  return sections.map((section, index) => ({
    ...section,
    order: typeof section.order === 'number' ? section.order : index,
    apps: (Array.isArray(section.apps) ? section.apps : []).map((app, appIndex) => ({
      ...app,
      id: app.id || `app-${section.id}-${appIndex + 1}`
    }))
  }));
}

function loadSections() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return normalize(parsed);
    }
  } catch {
    // fall through to seed data
  }
  return seedSections();
}

function loadQuickLaunchIds() {
  try {
    const value = JSON.parse(window.localStorage.getItem(QUICK_LAUNCH_KEY) || '[]');
    return Array.isArray(value) ? [...new Set(value.filter((id) => typeof id === 'string'))] : [];
  } catch {
    return [];
  }
}

function loadQuickApps() {
  try {
    const value = JSON.parse(window.localStorage.getItem(QUICK_APPS_KEY) || '[]');
    return Array.isArray(value) ? value.filter((app) => app?.id && app?.name && app?.url) : [];
  } catch {
    return [];
  }
}

function createUuid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  if (!globalThis.crypto?.getRandomValues) {
    throw new Error('Secure UUID generation is unavailable in this browser.');
  }

  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function getLegacyImportSnapshot() {
  let hasSections = false;
  let hasQuickLaunch = false;
  let hasQuickApps = false;

  try {
    hasSections = Boolean(window.localStorage.getItem(STORAGE_KEY));
    hasQuickLaunch = Boolean(window.localStorage.getItem(QUICK_LAUNCH_KEY));
    hasQuickApps = Boolean(window.localStorage.getItem(QUICK_APPS_KEY));
  } catch {
    return null;
  }

  if (!hasSections && !hasQuickLaunch && !hasQuickApps) return null;

  const storedSections = hasSections ? loadSections() : [];
  const sections = storedSections.map((section, index) => ({
    id: createUuid(),
    sourceId: section.id,
    name: String(section.title || 'Section').trim() || 'Section',
    display_order: Number.isFinite(section.order) ? section.order : index
  }));
  const sectionIds = new Map(sections.map((section) => [section.sourceId, section.id]));
  const applicationIds = new Map();
  const applications = [];

  storedSections.forEach((section, sectionIndex) => {
    section.apps.forEach((app, appIndex) => {
      const sourceId = app.id || createId(`legacy-${sectionIndex}-${appIndex}`);
      if (!applicationIds.has(sourceId)) applicationIds.set(sourceId, createUuid());
      if (applications.some((item) => item.id === applicationIds.get(sourceId))) return;
      applications.push({
        id: applicationIds.get(sourceId),
        name: String(app.name || 'Application'),
        url: String(app.url || ''),
        favicon_url: app.faviconUrl || null,
        section_id: sectionIds.get(section.id) || null,
        display_order: Number.isFinite(app.order) ? app.order : appIndex
      });
    });
  });

  loadQuickApps().forEach((app, index) => {
    const sourceId = app.id || createId(`legacy-quick-${index}`);
    if (!applicationIds.has(sourceId)) applicationIds.set(sourceId, createUuid());
    if (applications.some((item) => item.id === applicationIds.get(sourceId))) return;
    applications.push({
      id: applicationIds.get(sourceId),
      name: String(app.name || 'Application'),
      url: String(app.url || ''),
      favicon_url: app.faviconUrl || null,
      section_id: null,
      display_order: 0
    });
  });

  const knownApps = new Set(applications.map((app) => app.id));
  const quickLaunch = loadQuickLaunchIds()
    .map((id, index) => ({
      application_id: applicationIds.get(id),
      display_order: index
    }))
    .filter((item) => item.application_id && knownApps.has(item.application_id));

  if (!sections.length && !applications.length) return null;

  return {
    sections: sections.map(({ id, name, display_order }) => ({ id, name, display_order })),
    applications,
    quickLaunch
  };
}

const emptyData = {
  workspace: null,
  sections: [],
  allApps: [],
  vaultApps: [],
  quickLaunchIds: []
};

function useSections() {
  const [session, setSession] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [data, setData] = useState(emptyData);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [saveState, setSaveState] = useState('idle');
  const [saveError, setSaveError] = useState('');
  const authEventSeen = useRef(false);
  const activeUser = useRef(null);
  const saveTimer = useRef(null);
  const pending = useRef(0);
  const problem = useRef(null);

  useEffect(() => {
    let mounted = true;

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      authEventSeen.current = true;
      setSession(nextSession);
      setAuthReady(true);
    });

    supabase.auth.getSession().then(({ data: result, error }) => {
      if (!mounted || authEventSeen.current) return;
      if (error) setLoadError(error.message);
      setSession(result?.session || null);
      setAuthReady(true);
    });

    return () => {
      mounted = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  const signIn = useCallback(async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return error?.message || '';
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    return error?.message || '';
  }, []);

  const retryLoad = useCallback(() => setReloadToken((value) => value + 1), []);

  useEffect(() => {
    const userId = session?.user?.id || null;
    activeUser.current = userId;
    if (!userId) {
      setData(emptyData);
      setLoading(false);
      setLoadError('');
      setSaveState('idle');
      return undefined;
    }

    let mounted = true;
    setLoading(true);
    setLoadError('');

    loadWorkspace(userId)
      .then((workspaceData) => {
        if (mounted && activeUser.current === userId) {
          setData(workspaceData);
          setLoading(false);
          setLoadError('');
          setSaveState('idle');
          setSaveError('');
        }
      })
      .catch((error) => {
        if (mounted && activeUser.current === userId) {
          setLoadError(error.message || 'Unable to load workspace data.');
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
      window.clearTimeout(saveTimer.current);
    };
  }, [session?.user?.id, reloadToken]);

  // Write and refresh run together inside the queue, so writes, refreshes and
  // status updates always complete in submission order.
  const mutate = useCallback((operation) => {
    const knownWorkspace = data.workspace;
    const workspaceId = knownWorkspace?.id;
    const userId = session?.user?.id;
    if (!workspaceId || !userId) {
      return Promise.resolve({ saved: false, error: 'Your workspace is not ready yet.' });
    }

    if (pending.current === 0) problem.current = null;
    pending.current += 1;
    setSaveState('saving');
    setSaveError('');
    window.clearTimeout(saveTimer.current);

    const task = mutationQueue.then(async () => {
      let result;
      try {
        await operation(workspaceId, userId);
      } catch (error) {
        problem.current = { state: 'error', message: error.message || 'Your change could not be saved.' };
        result = { saved: false, error: error.message || 'Your change could not be saved.' };
      }

      if (!result) {
        if (pending.current > 1) {
          // A later queued operation will refresh and cover this change.
          result = { saved: true, refreshed: true };
        } else {
          try {
            const latest = await loadWorkspace(userId, knownWorkspace);
            if (activeUser.current === userId) {
              setData(latest);
              setLoadError('');
            }
            result = { saved: true, refreshed: true };
          } catch (error) {
            if (!problem.current) {
              problem.current = {
                state: 'refreshError',
                message: error.message || 'The change was saved, but the workspace could not refresh.'
              };
            }
            result = {
              saved: true,
              refreshed: false,
              refreshError: error.message || 'The change was saved, but the workspace could not refresh.'
            };
          }
        }
      }

      pending.current -= 1;
      if (pending.current === 0 && activeUser.current === userId) {
        if (problem.current) {
          setSaveState(problem.current.state);
          setSaveError(problem.current.message);
        } else {
          setSaveState('saved');
          saveTimer.current = window.setTimeout(() => setSaveState('idle'), 1800);
        }
      }
      return result;
    });
    mutationQueue = task.catch(() => undefined);
    return task;
  }, [data.workspace, session?.user?.id]);

  const addSection = useCallback((title) => mutate((workspaceId, ownerId) => (
    createRemoteSection(workspaceId, ownerId, title)
  )), [mutate]);

  const renameSection = useCallback((id, title) => mutate((workspaceId) => (
    renameRemoteSection(workspaceId, id, title)
  )), [mutate]);

  const deleteSection = useCallback((id) => mutate((workspaceId) => (
    deleteRemoteSection(workspaceId, id)
  )), [mutate]);

  const moveSection = useCallback((activeId, overId) => mutate((workspaceId) => (
    reorderSection(workspaceId, activeId, overId)
  )), [mutate]);

  const addApp = useCallback((sectionId, values) => mutate((workspaceId, ownerId) => (
    createApplication(workspaceId, ownerId, sectionId, values)
  )), [mutate]);

  const addVaultTool = useCallback((values) => mutate((workspaceId, ownerId) => (
    createVaultTool(workspaceId, ownerId, values)
  )), [mutate]);

  const updateVaultTool = useCallback((appId, values) => mutate((workspaceId) => (
    updateRemoteVaultTool(workspaceId, appId, values)
  )), [mutate]);

  const addQuickLaunchApp = useCallback((values) => mutate((workspaceId) => (
    createQuickLaunchApplication(workspaceId, values.name, values.url)
  )), [mutate]);

  const addToQuickLaunch = useCallback((appId) => mutate((workspaceId) => (
    pinApplication(workspaceId, appId)
  )), [mutate]);

  const removeFromQuickLaunch = useCallback((appId) => mutate((workspaceId) => (
    unpinApplication(workspaceId, appId)
  )), [mutate]);

  const moveQuickLaunch = useCallback((activeId, overId) => mutate((workspaceId) => (
    reorderQuickLaunch(workspaceId, activeId, overId)
  )), [mutate]);

  const updateApp = useCallback((appId, values) => mutate((workspaceId) => (
    updateApplication(workspaceId, appId, values)
  )), [mutate]);

  const deleteApp = useCallback((appId) => mutate((workspaceId) => (
    deleteApplication(workspaceId, appId)
  )), [mutate]);

  const moveApp = useCallback((appId, targetSectionId, index) => mutate((workspaceId) => (
    moveApplication(workspaceId, appId, targetSectionId, index)
  )), [mutate]);

  const saveAppForLater = useCallback((appId) => mutate((workspaceId) => (
    moveApplicationToVault(workspaceId, appId)
  )), [mutate]);

  const restoreVaultTool = useCallback((appId, targetSectionId) => mutate((workspaceId) => (
    restoreVaultApplication(workspaceId, appId, targetSectionId)
  )), [mutate]);

  const removeVaultTool = useCallback((appId) => mutate((workspaceId) => (
    deleteVaultToolRemote(workspaceId, appId)
  )), [mutate]);

  const importLegacy = useCallback((snapshot) => mutate((workspaceId) => (
    importLegacyWorkspace(workspaceId, snapshot)
  )), [mutate]);

  const quickLaunchApps = data.quickLaunchIds
    .map((id) => data.allApps.find((app) => app.id === id))
    .filter(Boolean);

  return {
    ...data,
    session,
    authReady,
    signIn,
    signOut,
    quickLaunchApps,
    loading,
    loadError,
    retryLoad,
    saveState,
    saveError,
    addSection,
    renameSection,
    deleteSection,
    moveSection,
    addApp,
    addQuickLaunchApp,
    addToQuickLaunch,
    removeFromQuickLaunch,
    moveQuickLaunch,
    updateApp,
    deleteApp,
    moveApp,
    saveAppForLater,
    addVaultTool,
    updateVaultTool,
    restoreVaultTool,
    removeVaultTool,
    importLegacy
  };
}

export default useSections;